/**
 * GroupDetailsContent Component
 *
 * Shared form content for managing group details.
 * Used by both GroupDetailsDialog (desktop) and ManageDetailsPage (mobile).
 */

import React, { ReactElement, useRef } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Paper,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import ListItemAutocomplete from "../../../components/ListItemAutocomplete";
import CannotChangeLaterIcon from "../../../components/CannotChangeLaterIcon";
import { SortableDetailCard } from "./GroupDetailsDialog";
import { TransactionCategoryDetail, TransactionCategory, TransactionCategoryGroup, Transaction } from "../../../types";

export interface GroupDetailsContentProps {
  groupName: string;
  editingGroupDetails: TransactionCategoryDetail[];
  setEditingGroupDetails: (details: TransactionCategoryDetail[]) => void;
  newDetailName: string;
  setNewDetailName: (name: string) => void;
  newDetailType: "list" | "measurable" | "";
  setNewDetailType: (type: "list" | "measurable" | "") => void;
  newDetailUnit: string;
  setNewDetailUnit: (unit: string) => void;
  newListItems: string[];
  setNewListItems: (items: string[]) => void;
  detailNameError: string;
  setDetailNameError: (error: string) => void;
  editingDetailIndex: number | null;
  setEditingDetailIndex: (index: number | null) => void;
  editingOriginalDetail: TransactionCategoryDetail | null;
  setEditingOriginalDetail: (detail: TransactionCategoryDetail | null) => void;
  savingDetails: boolean;
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  transactions: Transaction[];
  isGroupDetailUsed: (groupName: string, detailName: string) => boolean;
  hasPartialFormData: (
    newDetailName: string,
    newDetailType: "list" | "measurable" | "",
    newDetailUnit: string,
    newListItems: string[],
    editingDetailIndex: number | null
  ) => boolean;
  hasEditedDetailChanged: (
    editingDetailIndex: number | null,
    editingOriginalDetail: TransactionCategoryDetail | null,
    newDetailName: string,
    newDetailType: "list" | "measurable" | "",
    newDetailUnit: string,
    newListItems: string[]
  ) => boolean;
  onDragEnd: (event: DragEndEvent) => void;
  onEditDetail: (index: number) => void;
  onRemoveDetail: (index: number) => void;
}

export function GroupDetailsContent(props: GroupDetailsContentProps): ReactElement {
  const {
    groupName,
    editingGroupDetails,
    setEditingGroupDetails,
    newDetailName,
    setNewDetailName,
    newDetailType,
    setNewDetailType,
    newDetailUnit,
    setNewDetailUnit,
    newListItems,
    setNewListItems,
    detailNameError,
    setDetailNameError,
    editingDetailIndex,
    setEditingDetailIndex,
    editingOriginalDetail,
    setEditingOriginalDetail,
    savingDetails,
    categories,
    groups,
    transactions,
    isGroupDetailUsed,
    hasPartialFormData,
    hasEditedDetailChanged,
    onDragEnd,
    onEditDetail,
    onRemoveDetail,
  } = props;

  const detailTypeSelectRef = useRef<HTMLInputElement | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddOrUpdateDetail = (): void => {
    if (!newDetailName.trim()) {
      setDetailNameError("Detail name is required");
      return;
    }

    if (!newDetailType) {
      setDetailNameError("Detail type is required");
      return;
    }

    if (newDetailType === "measurable" && !newDetailUnit.trim()) {
      setDetailNameError("Unit is required for measurable details");
      return;
    }

    if (newDetailType === "list" && newListItems.length === 0) {
      setDetailNameError("At least one list item is required");
      return;
    }

    if (editingDetailIndex !== null) {
      const nameConflict = editingGroupDetails.some(
        (d, i) => i !== editingDetailIndex && d.name.toLowerCase() === newDetailName.trim().toLowerCase()
      );
      if (nameConflict) {
        setDetailNameError("A detail with this name already exists.");
        return;
      }
    } else {
      if (editingGroupDetails.some((d) => d.name.toLowerCase() === newDetailName.trim().toLowerCase())) {
        setDetailNameError("A detail with this name already exists.");
        return;
      }
    }

    const newDetail: TransactionCategoryDetail = {
      name: newDetailName.trim(),
      type: newDetailType,
      ...(newDetailType === "list" ? { listItems: [...newListItems] } : {}),
      ...(newDetailType === "measurable" ? { unit: newDetailUnit.trim() } : {}),
    };

    if (editingDetailIndex !== null) {
      const updatedDetails = [...editingGroupDetails];
      updatedDetails[editingDetailIndex] = newDetail;
      setEditingGroupDetails(updatedDetails);
    } else {
      setEditingGroupDetails([...editingGroupDetails, newDetail]);
    }

    setNewDetailName("");
    setNewDetailType("");
    setNewDetailUnit("");
    setNewListItems([]);
    setDetailNameError("");
    setEditingDetailIndex(null);
    setEditingOriginalDetail(null);
  };

  return (
    <Box>
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
          {editingDetailIndex !== null ? "Edit Detail" : "Add New Detail"}
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Detail Name"
            value={newDetailName}
            onChange={(e) => {
              const value = e.target.value;
              setNewDetailName(value);
              const trimmedName = value.trim();
              if (trimmedName) {
                const duplicate = editingGroupDetails.some(
                  (d, i) => i !== editingDetailIndex && d.name.toLowerCase() === trimmedName.toLowerCase()
                );
                if (duplicate) {
                  setDetailNameError("A detail with this name already exists.");
                } else {
                  setDetailNameError("");
                }
              } else {
                setDetailNameError("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                detailTypeSelectRef.current?.focus();
              }
            }}
            fullWidth
            placeholder="e.g., Location, Car, Odometer"
            error={!!detailNameError}
            helperText={detailNameError}
            disabled={savingDetails}
            sx={{
              "& .MuiFormHelperText-root": {
                fontSize: "0.75rem",
              },
            }}
          />
          <FormControl fullWidth sx={{ position: "relative" }}>
            <InputLabel>Detail Type</InputLabel>
            <Select
              inputRef={detailTypeSelectRef}
              value={newDetailType}
              onChange={(e) => {
                const newType = e.target.value as "list" | "measurable";
                setNewDetailType(newType);
                if (newType !== "list") {
                  setNewListItems([]);
                }
                if (newType !== "measurable") {
                  setNewDetailUnit("");
                }
              }}
              label="Detail Type"
              disabled={savingDetails || editingDetailIndex !== null}
              MenuProps={{
                disablePortal: true,
                MenuListProps: {
                  onWheel: (e: React.WheelEvent) => {
                    const listbox = e.currentTarget;
                    if (listbox.scrollHeight <= listbox.clientHeight) return;
                    e.stopPropagation();
                    e.preventDefault();
                    listbox.scrollTop += e.deltaY;
                  },
                },
              }}
            >
              <MenuItem value="">Select type...</MenuItem>
              <MenuItem value="list">List (Selectable values)</MenuItem>
              <MenuItem value="measurable">Measurable (Unit)</MenuItem>
            </Select>
            <CannotChangeLaterIcon
              tooltipMessage="Detail type cannot be changed after creation"
            />
          </FormControl>
          {newDetailType === "list" && (
            <ListItemAutocomplete
              items={newListItems}
              onChange={setNewListItems}
              label="List Items"
              placeholder="Type to add items (e.g., Audi TT, BMW E92)"
              detailName={
                editingDetailIndex !== null && editingOriginalDetail
                  ? editingOriginalDetail.name
                  : undefined
              }
              groupName={groupName}
              categories={categories.filter((cat) => {
                const group = cat.groupId ? groups.find((g) => g.id === cat.groupId) : null;
                return group?.name === groupName;
              })}
              transactions={transactions}
            />
          )}
          {newDetailType === "measurable" && (
            <TextField
              label="Unit of Measure"
              value={newDetailUnit}
              onChange={(e) => setNewDetailUnit(e.target.value)}
              fullWidth
              disabled={savingDetails}
              placeholder="e.g., km, L, kg, m"
              helperText="Enter the unit for this measurable detail (e.g., km for distance, L for volume)"
            />
          )}
          <Stack direction="row" spacing={1}>
            {(editingDetailIndex !== null || hasPartialFormData(newDetailName, newDetailType, newDetailUnit, newListItems, editingDetailIndex)) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setNewDetailName("");
                  setNewDetailType("");
                  setNewDetailUnit("");
                  setNewListItems([]);
                  setDetailNameError("");
                  setEditingDetailIndex(null);
                  setEditingOriginalDetail(null);
                }}
                sx={{ flex: 1 }}
                disabled={savingDetails}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleAddOrUpdateDetail}
              disabled={
                !newDetailName.trim() ||
                !newDetailType ||
                savingDetails ||
                (editingDetailIndex !== null &&
                  !hasEditedDetailChanged(editingDetailIndex, editingOriginalDetail, newDetailName, newDetailType, newDetailUnit, newListItems))
              }
              sx={{ flex: 1 }}
            >
              {editingDetailIndex !== null ? "Update Detail" : "Add Detail"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {editingGroupDetails.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Existing Details
            </Typography>
            <Box
              sx={{
                maxHeight: editingGroupDetails.length >= 3 ? "240px" : "none",
                overflowY: editingGroupDetails.length >= 3 ? "auto" : "visible",
                overflowX: "hidden",
                pr: editingGroupDetails.length >= 3 ? 1 : 0,
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={editingGroupDetails.map((_, index) => String(index))}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={1}>
                    {editingGroupDetails.map((detail, index) => {
                      const detailUsed = isGroupDetailUsed(groupName, detail.name);
                      return (
                        <SortableDetailCard
                          key={index}
                          detail={detail}
                          index={index}
                          categoryName={groupName}
                          isDetailUsed={detailUsed}
                          onEditDetail={onEditDetail}
                          onRemoveDetail={onRemoveDetail}
                          editingDetailIndex={editingDetailIndex}
                        />
                      );
                    })}
                  </Stack>
                </SortableContext>
              </DndContext>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
